import React, { useState, useEffect } from 'react';
import './BusinessAccountModal.css';

const BusinessAccountModal = ({ isOpen, onClose, onSave, account = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    messageTemplateNamespace: '',
    currency: 'USD',
    timezoneId: 'America/New_York',
    ownerBusinessId: '',
    ownerBusinessName: '',
    enableMultiPartner: false,
    partners: []
  });
  const [errors, setErrors] = useState({});

  const isEditMode = !!account;

  const currencies = ['USD', 'EUR', 'GBP', 'INR', 'AED', 'SAR'];
  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'GMT (London)' },
    { value: 'Europe/Paris', label: 'CET (Paris)' },
    { value: 'Asia/Dubai', label: 'GST (Dubai)' },
    { value: 'Asia/Kolkata', label: 'IST (India)' },
    { value: 'UTC', label: 'UTC' }
  ];

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name || '',
        messageTemplateNamespace: account.messageTemplateNamespace || '',
        currency: account.currency || 'USD',
        timezoneId: account.timezoneId || 'America/New_York',
        ownerBusinessId: account.ownerBusinessId || '',
        ownerBusinessName: account.ownerBusinessName || '',
        enableMultiPartner: (account.partners && account.partners.length > 0) || false,
        partners: account.partners || []
      });
    } else {
      setFormData({
        name: '',
        messageTemplateNamespace: '',
        currency: 'USD',
        timezoneId: 'America/New_York',
        ownerBusinessId: '',
        ownerBusinessName: '',
        enableMultiPartner: false,
        partners: []
      });
    }
    setErrors({});
  }, [account, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target || e;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    }

    if (!formData.messageTemplateNamespace.trim()) {
      newErrors.messageTemplateNamespace = 'Namespace is required';
    } else if (!/^[a-z0-9_]+$/.test(formData.messageTemplateNamespace)) {
      newErrors.messageTemplateNamespace = 'Namespace must contain only lowercase letters, numbers, and underscores';
    }

    if (!formData.ownerBusinessId.trim()) {
      newErrors.ownerBusinessId = 'Owner Business ID is required';
    }

    if (!formData.ownerBusinessName.trim()) {
      newErrors.ownerBusinessName = 'Owner Business Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content business-account-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Business Account' : 'Create Business Account'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="name">Account Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Main Business Account"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="messageTemplateNamespace">Message Template Namespace *</label>
            <input
              type="text"
              id="messageTemplateNamespace"
              name="messageTemplateNamespace"
              value={formData.messageTemplateNamespace}
              onChange={handleChange}
              placeholder="business_namespace"
              className={errors.messageTemplateNamespace ? 'error' : ''}
            />
            {errors.messageTemplateNamespace && <span className="error-message">{errors.messageTemplateNamespace}</span>}
            <span className="help-text">Lowercase letters, numbers, and underscores only</span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="currency">Currency *</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="form-select"
              >
                {currencies.map(currency => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="timezoneId">Timezone *</label>
              <select
                id="timezoneId"
                name="timezoneId"
                value={formData.timezoneId}
                onChange={handleChange}
                className="form-select"
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="ownerBusinessId">Owner Business ID *</label>
            <input
              type="text"
              id="ownerBusinessId"
              name="ownerBusinessId"
              value={formData.ownerBusinessId}
              onChange={handleChange}
              placeholder="987654321098765"
              className={errors.ownerBusinessId ? 'error' : ''}
            />
            {errors.ownerBusinessId && <span className="error-message">{errors.ownerBusinessId}</span>}
            <span className="help-text">The Meta Business ID that owns this WABA</span>
          </div>

          <div className="form-group">
            <label htmlFor="ownerBusinessName">Owner Business Name *</label>
            <input
              type="text"
              id="ownerBusinessName"
              name="ownerBusinessName"
              value={formData.ownerBusinessName}
              onChange={handleChange}
              placeholder="My Business"
              className={errors.ownerBusinessName ? 'error' : ''}
            />
            {errors.ownerBusinessName && <span className="error-message">{errors.ownerBusinessName}</span>}
          </div>

          <div className="form-group">
            <div className="toggle-item">
              <div className="toggle-info">
                <label>Enable Multi-Partner Solutions</label>
                <span className="toggle-description">Allow multiple solution providers to collaborate on this account</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="enableMultiPartner"
                  checked={formData.enableMultiPartner}
                  onChange={handleChange}
                />
                <span className="slider"></span>
              </label>
            </div>
            <span className="help-text">
              Multi-partner solutions allow businesses to work with multiple solution providers while maintaining compliance and security. 
              <a href="https://developers.facebook.com/docs/whatsapp/business-management-api/multi-partner-solutions" target="_blank" rel="noopener noreferrer" className="help-link">
                Learn more
              </a>
            </span>
          </div>

          {isEditMode && (
            <div className="info-section">
              <h3>Account Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">WABA ID:</span>
                  <span className="info-value">{account.id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Account Status:</span>
                  <span className="info-value">{account.accountStatus || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Verification Status:</span>
                  <span className="info-value">{account.businessVerificationStatus || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone Numbers:</span>
                  <span className="info-value">{account.phoneNumbers || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Partners:</span>
                  <span className="info-value">{account.partnerCount || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Created At:</span>
                  <span className="info-value">
                    {account.createdAt 
                      ? new Date(account.createdAt).toLocaleString()
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              {isEditMode ? 'Update Account' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessAccountModal;

